"use client";

import {
  Gauge,
  Loader2,
  AlertTriangle,
  Lightbulb,
  ArrowLeft,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/shadcn/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { cn } from "@/lib/utils";
import {
  HeuristicEvaluationResponse,
  HeuristicScoreResponse,
} from "@/generated";
import { useState, useCallback } from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import { Badge } from "@/components/shadcn/badge";
import { Separator } from "@/components/shadcn/separator";

interface HeuristicGaugeProps {
  isLoading: boolean;
  results: HeuristicEvaluationResponse | null;
  showPopover: boolean;
  onPopoverToggle: (open: boolean) => void;
  onImproveFromHeuristics?: (results: HeuristicEvaluationResponse) => void;
  isImproving?: boolean;
  isOutdated?: boolean;
  isError?: boolean;
  errorMessage?: string | null;
  onTriggerEvaluation?: () => void;
  className?: string;
}

export const HEURISTIC_CONFIG: Record<
  string,
  { label: string; shortLabel: string; description: string }
> = {
  clarity: {
    label: "Clarity",
    shortLabel: "Clarity",
    description: "How clear and understandable the instructions are.",
  },
  ambiguity: {
    label: "Unambiguity",
    shortLabel: "Unambiguity",
    description: "Absence of vague or open-ended terms.",
  },
  specificity: {
    label: "Specificity",
    shortLabel: "Specificity",
    description: "Level of detail and concrete constraints.",
  },
  actionability: {
    label: "Actionability",
    shortLabel: "Actionability",
    description: "Clear steps or actions to be taken.",
  },
  task_breakdown: {
    label: "Structure",
    shortLabel: "Structure",
    description: "Logical flow and breakdown of complex tasks.",
  },
  roleplaying: {
    label: "Persona",
    shortLabel: "Persona",
    description: "Definition of persona and context.",
  },
};

export const DEFAULT_HEURISTIC_ORDER = [
  "specificity",
  "clarity",
  "ambiguity",
  "roleplaying",
  "task_breakdown",
  "actionability",
];

export function ScoreRing({
  score,
  maxScore = 100,
  size = 60,
  strokeWidth = 4,
  hideValue = false,
}: {
  score: number;
  maxScore?: number;
  size?: number;
  strokeWidth?: number;
  hideValue?: boolean;
}) {
  const percentage = Math.min(100, Math.max(0, (score / maxScore) * 100));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const getColor = () => {
    if (percentage >= 75) return "text-emerald-500";
    if (percentage >= 40) return "text-warning-500";
    return "text-rose-500";
  };

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg
        className="transform -rotate-90 w-full h-full"
        viewBox={`0 0 ${size} ${size}`}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-muted/20"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className={cn("transition-all duration-1000 ease-out", getColor())}
        />
      </svg>
      {!hideValue && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className={cn(
              "font-semibold tabular-nums tracking-tight",
              size <= 30 ? "text-[9px] leading-none" : "text-sm",
              getColor()
            )}
          >
            {Math.round(score)}
          </span>
        </div>
      )}
    </div>
  );
}

export function HeuristicFeedback({
  heuristicKey,
  score,
}: {
  heuristicKey: string;
  score: HeuristicScoreResponse;
}) {
  const config = HEURISTIC_CONFIG[heuristicKey];

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-200">
      <div className="space-y-1 mb-6">
        <h4 className="text-base font-medium tracking-tight">{config.label}</h4>
        <p className="text-xs text-muted-foreground leading-normal">
          {config.description}
        </p>
      </div>

      <div className="space-y-6">
        {score.reasoning && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-warning-600 dark:text-warning-500">
              <AlertTriangle size={16} />
              <span>Critique</span>
            </div>
            <div className="text-xs text-muted-foreground leading-normal bg-background2 p-3 rounded-md border border-border">
              {score.reasoning}
            </div>
          </div>
        )}

        {score.suggestions && score.suggestions.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-emerald-600 dark:text-emerald-500">
              <Lightbulb size={16} />
              <span>Suggestions</span>
            </div>
            <ul className="text-xs text-muted-foreground leading-normal space-y-1.5 border border-border p-3 rounded-md bg-background2">
              {score.suggestions.map((suggestion, idx) => (
                <li key={idx} className="flex gap-2">
                  <span className="text-emerald-500 -mt-0.25">•</span>
                  <span>{suggestion}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export function getGaugeButtonStyle(weightedScore: number): string {
  const percentage = (weightedScore / 10) * 100;
  if (percentage >= 75) {
    return "text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40";
  } else if (percentage >= 40) {
    return "text-warning-600 dark:text-warning-400 border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 hover:bg-warning-100 dark:hover:bg-warning-900/40";
  }
  return "text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 hover:bg-rose-100 dark:hover:bg-rose-900/40";
}

export function buildRadarData(
  results: HeuristicEvaluationResponse | null,
  evaluatedTypes?: string[]
) {
  const types = evaluatedTypes ?? DEFAULT_HEURISTIC_ORDER;
  const heuristicOrder = DEFAULT_HEURISTIC_ORDER.filter((h) =>
    types.includes(h)
  );
  const hasResults = results !== null;

  return heuristicOrder.map((key) => {
    const scoreData = results?.[key as keyof HeuristicEvaluationResponse] as
      | HeuristicScoreResponse
      | null
      | undefined;
    const score =
      hasResults && scoreData
        ? key === "ambiguity"
          ? 10 - scoreData.score
          : scoreData.score
        : 0;
    return {
      subject: HEURISTIC_CONFIG[key].shortLabel,
      value: score,
      fullMark: 10,
    };
  });
}

export function HeuristicGaugeButton({
  isLoading,
  results,
  showPopover,
  onPopoverToggle,
  onImproveFromHeuristics,
  isImproving,
  isOutdated,
  isError,
  errorMessage,
  onTriggerEvaluation,
  className,
}: HeuristicGaugeProps) {
  const hasResults = results !== null;
  const weightedScore = results?.weighted_score ?? 0;
  const [selectedHeuristic, setSelectedHeuristic] = useState<string | null>(
    null
  );

  // Filter heuristic order to only include evaluated types
  const evaluatedTypes = results?.evaluated_types ?? DEFAULT_HEURISTIC_ORDER;
  const heuristicOrder = DEFAULT_HEURISTIC_ORDER.filter((h) =>
    evaluatedTypes.includes(h)
  );

  const radarData = buildRadarData(results, evaluatedTypes);

  const getSelectedScore = useCallback((): HeuristicScoreResponse | null => {
    if (!selectedHeuristic || !results) return null;
    const scoreData =
      results[selectedHeuristic as keyof HeuristicEvaluationResponse];
    if (scoreData && typeof scoreData === "object" && "score" in scoreData) {
      return scoreData as HeuristicScoreResponse;
    }
    return null;
  }, [selectedHeuristic, results]);

  const selectedScore = getSelectedScore();

  const handleLabelClick = useCallback(
    (data: { index?: number }) => {
      if (data.index !== undefined && data.index < heuristicOrder.length) {
        setSelectedHeuristic(heuristicOrder[data.index]);
      }
    },
    [heuristicOrder]
  );

  return (
    <Popover open={showPopover} onOpenChange={onPopoverToggle}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className={cn(
            "w-6 h-6 rounded-md transition-all duration-200 shadow-sm",
            isError && !isLoading
              ? "text-destructive border-red-200 bg-red-50 hover:bg-red-100"
              : hasResults
                ? getGaugeButtonStyle(weightedScore)
                : "bg-background hover:bg-muted text-muted-foreground",
            isLoading && "animate-pulse opacity-70",
            isOutdated && hasResults && "opacity-60 border-dashed",
            className
          )}
          disabled={isLoading && !hasResults}
        >
          {isLoading ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Gauge size={14} strokeWidth={2} />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[340px] p-0 overflow-hidden shadow-xl border-border/60"
        align="end"
        side="top"
        sideOffset={8}
      >
        <div
          className="flex flex-col max-h-[70vh]"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {isError && !isLoading ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center space-y-3 bg-muted/10">
              <div className="p-3 rounded-full bg-red-50">
                <AlertTriangle size={24} className="text-destructive" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  Analysis failed
                </p>
                <p className="text-xs text-muted-foreground">
                  {errorMessage ?? "Something went wrong. Please try again."}
                </p>
              </div>
              {onTriggerEvaluation && (
                <Button
                  onClick={onTriggerEvaluation}
                  size="sm"
                  variant="outline"
                  className="mt-2"
                >
                  <Sparkles size={14} />
                  Retry analysis
                </Button>
              )}
            </div>
          ) : !hasResults ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center space-y-3 bg-muted/10">
              <div className="p-3 rounded-full bg-muted/20">
                <Gauge size={24} className="text-muted-foreground/50" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  No evaluation yet
                </p>
                <p className="text-xs text-muted-foreground">
                  Type at least 200 characters to see quality metrics
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between bg-muted/10">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Prompt Quality
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground font-medium">
                    Overall Score
                  </span>
                  <ScoreRing
                    score={weightedScore * 10}
                    maxScore={100}
                    size={28}
                    strokeWidth={3}
                  />
                </div>
              </div>

              {/* Main Content Area */}
              <div className="p-4">
                {selectedHeuristic && selectedScore ? (
                  <HeuristicFeedback
                    heuristicKey={selectedHeuristic}
                    score={selectedScore}
                  />
                ) : (
                  <div className="animate-in fade-in zoom-in-95 duration-200">
                    <div className="h-[220px] w-full relative -ml-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart
                          cx="50%"
                          cy="50%"
                          outerRadius="75%"
                          data={radarData}
                        >
                          <PolarGrid
                            stroke="currentColor"
                            strokeOpacity={0.1}
                            gridType="polygon"
                          />
                          <PolarAngleAxis
                            dataKey="subject"
                            label={{
                              fill: "currentColor",
                              fontSize: 10,
                              fontFamily: "var(--font-sans)",
                              offset: 100,
                              cursor: "pointer",
                            }}
                            offset={100}
                            tick={{
                              fill: "currentColor",
                              fontSize: 10,
                              fontFamily: "var(--font-sans)",
                              opacity: 0.6,
                              cursor: "pointer",
                            }}
                            onClick={handleLabelClick}
                          />
                          <PolarRadiusAxis
                            angle={90}
                            domain={[0, 10]}
                            tick={false}
                            axisLine={false}
                          />
                          <Radar
                            name="Score"
                            dataKey="value"
                            stroke="#10b981"
                            strokeWidth={2}
                            fill="#10b981"
                            fillOpacity={0.15}
                            isAnimationActive={true}
                          />
                        </RadarChart>
                      </ResponsiveContainer>

                      {/* Overlay hint */}
                      <div className="absolute bottom-0 w-full text-center">
                        <p className="text-[10px] text-muted-foreground/60 italic">
                          Click on labels for details
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Improve from analysis / Re-analyse button */}
              {(onImproveFromHeuristics ||
                (isOutdated && onTriggerEvaluation)) && (
                <>
                  <Separator />
                  <div className="px-4 py-3">
                    {isOutdated && onTriggerEvaluation ? (
                      <Button
                        onClick={onTriggerEvaluation}
                        size="sm"
                        className="w-full"
                      >
                        <Sparkles size={14} />
                        Re-analyse
                      </Button>
                    ) : (
                      onImproveFromHeuristics && (
                        <Button
                          onClick={() => onImproveFromHeuristics(results)}
                          disabled={isImproving}
                          size="sm"
                          className="w-full"
                        >
                          <Sparkles size={14} />
                          {isImproving
                            ? "Improving..."
                            : "Improve from analysis"}
                        </Button>
                      )
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default HeuristicGaugeButton;
